import { message } from "antd";
import Joi from "joi-browser";
import { useMemo, useState } from "react";
import Form from "V2.0/components/Form";
import { putTTSSettings } from "../../service";

interface ElevenlabSettings {
  elevenlabsModel: string;
  voiceStability: number;
  voiceSimilarityBoost: number;
  voiceSpeed: number;
  voiceStyle: number;
}

const elevenlabModels = {
  eleven_turbo_v2_5: {
    label: "eleven_turbo_v2_5 (Multilingual)",
    value: "eleven_turbo_v2_5",
    id: "eleven_turbo_v2_5",
  },
  eleven_turbo_v2: {
    label: "eleven_turbo_v2 (English only)",
    value: "eleven_turbo_v2",
    id: "eleven_turbo_v2",
  },
  eleven_flash_v2_5: {
    label: "eleven_flash_v2_5 (Multilingual)",
    value: "eleven_flash_v2_5",
    id: "eleven_flash_v2_5",
  },
  eleven_flash_v2: {
    label: "eleven_flash_v2 (English only)",
    value: "eleven_flash_v2",
    id: "eleven_flash_v2",
  },
};

const template = [
  {
    name: "elevenlabsModel",
    label: "Model Name",
    type: "select",
    options: Object.values(elevenlabModels),
  },
  {
    name: "voiceStability",
    label: "Stability",
    description:
      "Lower value - broader emotion, Higher value - monotonous and limited",
    type: "slider",
    min: 0,
    max: 1,
    step: 0.1,
  },
  {
    name: "voiceSimilarityBoost",
    label: "Similarity",
    description:
      "Determines how closely the AI should adhere to the original voice when attempting to replicate it.",
    type: "slider",
    min: 0,
    max: 1,
    step: 0.1,
  },
  {
    name: "voiceSpeed",
    label: "Speed",
    description: "Controls the speed of the generated speech",
    type: "slider",
    min: 0.7,
    max: 1.2,
    step: 0.1,
  },
  {
    name: "voiceStyle",
    label: "Style",
    description:
      "Amplifies the style of the original speaker (increases latency)",
    type: "slider",
    min: 0,
    max: 1,
    step: 0.1,
  },
];

const schema = {
  elevenlabsModel: Joi.string(),
  voiceStability: Joi.number(),
  voiceSimilarityBoost: Joi.number(),
  voiceSpeed: Joi.number(),
  voiceStyle: Joi.number(),
};

function getElevenlabConfigurableSettings(settings) {
  const {
    elevenlabsModel,
    voiceStability,
    voiceSpeed,
    voiceSimilarityBoost,
    voiceStyle,
  } = settings;

  return {
    elevenlabsModel,
    voiceSimilarityBoost,
    voiceSpeed,
    voiceStability,
    voiceStyle,
  };
}

function Elevenlabs(props) {
  const {
    elevenlabsSettings,
    ttsServiceProvider,
    syncAssistant,
    workspaceId,
    assistantId,
    onClose,
  } = props;

  const [loading, setLoading] = useState(false);
  const [errorMessage] = useState({});

  const inputField = useMemo(() => {
    return getElevenlabConfigurableSettings(elevenlabsSettings);
  }, [elevenlabsSettings]);

  const handleSaveSettings = async (data) => {
    setLoading(true);
    message.loading("Saving");

    const payload = {
      ttsServiceProvider,
      elevenlabsSettings: data,
    };

    try {
      const response = await putTTSSettings({
        workspaceId,
        assistantId,
        payload,
      });

      if (response.status === 200) {
        message.destroy();
        setLoading(false);
        message.success("Saved");
        syncAssistant((prevAssistant) => {
          return { ...prevAssistant, ttsSettings: response.data };
        });
        onClose();
      }
    } catch (e) {
      setLoading(false);

      message.destroy();
      message.error(e.response.data?.msg || e.response.data?.message || e.response.data?.error || "Failed to save settings");
    }
  };

  return (
    <div>
      <Form
        inputField={inputField}
        template={template}
        schema={schema}
        buttonInfo={{ label: "Save", style: "right-side" }}
        loading={loading}
        setLoading={setLoading}
        onSubmit={handleSaveSettings}
      />
    </div>
  );
}

export default Elevenlabs;
